import { PlaybackProgress } from '@/components/admin/npc/watch/playback-progress';
import { type PageMark, speedLabel } from '@/lib/npc-playback';
import { cn } from '@/lib/utils';
import type { NpcPlaybackSpeed } from '@matter_extractor/shared';
import { NPC_PLAYBACK_SPEEDS } from '@matter_extractor/shared';
import { Icon } from '@pms-digital/design-system';

/**
 * La seconde rangée de la barre en différé (npc.md §11 ; maquette `Regarder`, « A, différé ») :
 * lecture et pause, la vitesse - ×1, ×2, ×4 -, « Page suivante », la ligne de progression marquée
 * page par page et sa légende « page 4 / 8 · tour 7 / 22 ».
 *
 * Aucun appel de modèle n'y est fait : la suite enregistrée se rejoue localement, rien ne se paie en
 * différé. Les boutons sont de vrais boutons, la vitesse un groupe où une seule est `aria-pressed`.
 */

export interface PlaybackControlsProps {
  playing: boolean;
  speed: NpcPlaybackSpeed;
  at: number;
  length: number;
  marks: readonly PageMark[];
  position: string;
  hasNext: boolean;
  onToggle: () => void;
  onSpeed: (speed: NpcPlaybackSpeed) => void;
  onNext: () => void;
  onSeek: (at: number) => void;
}

const BUTTON =
  'inline-flex h-7 cursor-pointer items-center gap-1 rounded-md border border-white/40 bg-transparent px-2.5 font-semibold text-white text-caption hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-not-allowed disabled:opacity-50';

export function PlaybackControls({
  playing,
  speed,
  at,
  length,
  marks,
  position,
  hasNext,
  onToggle,
  onSpeed,
  onNext,
  onSeek,
}: PlaybackControlsProps) {
  return (
    <div
      data-testid="regarder-lecture"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 border-white/15 border-t px-3 py-1.5 sm:px-4"
    >
      <button
        type="button"
        data-testid="regarder-lecture-pause"
        aria-label={playing ? 'Pause' : 'Lecture'}
        onClick={onToggle}
        className="inline-flex size-7 cursor-pointer items-center justify-center rounded-full bg-white text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
      >
        {playing ? (
          <Icon name="pause" size={14} filled />
        ) : (
          <Icon name="play_arrow" size={14} filled />
        )}
      </button>
      <fieldset className="m-0 flex border-0 p-0" aria-label="Vitesse de lecture">
        {NPC_PLAYBACK_SPEEDS.map((value) => (
          <button
            key={value}
            type="button"
            data-testid={`regarder-vitesse-${value}`}
            aria-pressed={speed === value}
            onClick={() => onSpeed(value)}
            className={cn(
              'h-7 cursor-pointer border border-white/40 px-2.5 font-semibold text-caption first:rounded-l-md last:rounded-r-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit',
              speed === value
                ? 'bg-white text-gray-900'
                : 'bg-transparent text-white hover:bg-white/10',
            )}
          >
            {speedLabel(value)}
          </button>
        ))}
      </fieldset>
      <button
        type="button"
        data-testid="regarder-page-suivante"
        disabled={!hasNext}
        onClick={onNext}
        className={BUTTON}
      >
        Page suivante
        <Icon name="chevron_right" size={14} />
      </button>
      <PlaybackProgress at={at} length={length} marks={marks} onSeek={onSeek} />
      <span data-testid="regarder-position" className="text-caption text-white/80">
        {position}
      </span>
    </div>
  );
}
